/**
 * Sign in with your company — `/sign-in-company`
 * Allows users to discover their company's organization by slug and sign in via
 * Google OAuth with the `hd` domain hint pre-filled.
 */

import { useCallback } from 'react'
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from 'react-native'
import { useRouter, type Href } from 'expo-router'
import Svg, { Path } from 'react-native-svg'
import { SignInFlowSafeArea } from '../src/signIn/components/SignInFlowSafeArea'
import { SignInAuthScreenLayout } from '../src/signIn/components/SignInAuthScreenLayout'
import { useCompanySignIn } from '../src/signIn/hooks/useCompanySignIn'
import { ErrorBoundary } from '../components/layout'

const ACCENT = '#A78BFA'
const PLACEHOLDER_COLOR = '#5A5A60'

const GoogleLogo = () => (
  <View style={s.googleLogoContainer}>
    <Svg width={18} height={18} viewBox="0 0 24 24">
      <Path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <Path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <Path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <Path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </Svg>
  </View>
)

function CompanySignInContent() {
  const router = useRouter()
  const {
    slug,
    setSlug,
    org,
    lookupLoading,
    lookupError,
    signInLoading,
    lookupOrg,
    handleCompanyGoogleAuth,
  } = useCompanySignIn()

  const handleBack = useCallback(() => {
    router.replace('/sign-in' as Href)
  }, [router])

  return (
    <SignInFlowSafeArea>
      <SignInAuthScreenLayout>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={s.inner}
        >
          <View style={s.card}>
            <Pressable onPress={handleBack} accessibilityLabel="Back to sign in" style={s.backBtn}>
              <Text style={s.backBtnText}>← Back</Text>
            </Pressable>

            <Text style={s.title}>Sign in with your company</Text>
            <Text style={s.subtitle}>
              Enter your company ID to sign in with your work Google account.
            </Text>

            <View style={s.inputRow}>
              <TextInput
                style={s.input}
                placeholder="company-id (e.g. acme)"
                placeholderTextColor={PLACEHOLDER_COLOR}
                value={slug}
                onChangeText={setSlug}
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="off"
                returnKeyType="search"
                onSubmitEditing={lookupOrg}
                editable={!lookupLoading && !signInLoading}
              />
              <Pressable
                style={[s.lookupBtn, (lookupLoading || !slug.trim()) && s.btnDisabled]}
                onPress={lookupOrg}
                disabled={lookupLoading || !slug.trim()}
                accessibilityLabel="Find organization"
              >
                {lookupLoading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={s.lookupBtnText}>Find</Text>
                )}
              </Pressable>
            </View>

            {lookupError != null && (
              <Text style={s.errorText}>{lookupError}</Text>
            )}

            {org != null && (
              <View style={s.orgCard}>
                <Text style={s.orgName}>{org.displayName ?? org.name}</Text>
                {org.allowedEmailDomain != null && (
                  <Text style={s.orgDomain}>@{org.allowedEmailDomain}</Text>
                )}

                <Pressable
                  style={[s.googleBtn, signInLoading && s.btnDisabled]}
                  onPress={handleCompanyGoogleAuth}
                  disabled={signInLoading}
                  accessibilityLabel="Sign in with Google for your company"
                >
                  {signInLoading ? (
                    <ActivityIndicator size="small" color={ACCENT} />
                  ) : (
                    <>
                      <GoogleLogo />
                      <Text style={s.googleBtnText}>
                        Continue with Google
                        {org.allowedEmailDomain ? ` (@${org.allowedEmailDomain})` : ''}
                      </Text>
                    </>
                  )}
                </Pressable>
              </View>
            )}
          </View>
        </KeyboardAvoidingView>
      </SignInAuthScreenLayout>
    </SignInFlowSafeArea>
  )
}

export default function CompanySignInScreen() {
  return (
    <ErrorBoundary>
      <CompanySignInContent />
    </ErrorBoundary>
  )
}

const s = StyleSheet.create({
  inner: { flex: 1 },
  card: {
    backgroundColor: '#1A1A24',
    borderRadius: 16,
    padding: 24,
    gap: 16,
  },
  backBtn: { alignSelf: 'flex-start' },
  backBtnText: { color: ACCENT, fontSize: 14 },
  title: { color: '#fff', fontSize: 22, fontWeight: '700' },
  subtitle: { color: '#9CA3AF', fontSize: 14, lineHeight: 20 },
  inputRow: { flexDirection: 'row', gap: 8 },
  input: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#2D2D3A',
    backgroundColor: '#13131C',
    color: '#fff',
    paddingHorizontal: 14,
    fontSize: 15,
  },
  lookupBtn: {
    height: 46,
    paddingHorizontal: 16,
    backgroundColor: ACCENT,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 64,
  },
  lookupBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },
  btnDisabled: { opacity: 0.5 },
  errorText: { color: '#F87171', fontSize: 13 },
  orgCard: {
    backgroundColor: '#13131C',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#2D2D3A',
  },
  orgName: { color: '#fff', fontSize: 17, fontWeight: '600' },
  orgDomain: { color: '#9CA3AF', fontSize: 13 },
  googleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#3D3D50',
    backgroundColor: '#1E1E2D',
    gap: 10,
    marginTop: 4,
  },
  googleBtnText: { color: '#fff', fontSize: 14, fontWeight: '500' },
  googleLogoContainer: { width: 18, height: 18 },
})
